import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Package, Eye, Clock, CheckCircle, XCircle, Truck, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { formatDateTimeShort } from '@/lib/datetime';

const STATUS_MAP = {
    pending: { label: 'Pendiente', icon: Clock, color: 'text-yellow-400' },
    confirmed: { label: 'Confirmado', icon: CheckCircle, color: 'text-blue-400' },
    preparing: { label: 'Preparando', icon: Package, color: 'text-purple-400' },
    ready: { label: 'Listo', icon: Truck, color: 'text-green-400' },
    delivered: { label: 'Entregado', icon: CheckCircle, color: 'text-white' },
    cancelled: { label: 'Cancelado', icon: XCircle, color: 'text-red-400' },
};

export default function CustomerMyOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [payingId, setPayingId] = useState(null);
    const [payError, setPayError] = useState('');

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const res = await api.get('/customer/store/orders');
                setOrders(res.data.data || []);
            } catch {} finally { setLoading(false); }
        };
        fetchOrders();
    }, []);

    const openDetail = async (order) => {
        try {
            const res = await api.get(`/customer/store/orders/${order.id}`);
            setSelectedOrder(res.data.data);
        } catch {
            setSelectedOrder(order);
        }
        setDetailOpen(true);
    };

    const handlePay = async (orderId) => {
        setPayingId(orderId);
        setPayError('');
        try {
            const res = await api.post(`/customer/store/orders/${orderId}/pay`);
            if (res.data.redirect_url) {
                window.location.href = res.data.redirect_url;
            } else {
                openDetail({ id: orderId });
            }
        } catch (err) {
            setPayError(err.response?.data?.message || 'Error al procesar el pago.');
        } finally { setPayingId(null); }
    };

    const statusBadge = (status) => {
        const s = STATUS_MAP[status] || { label: status, icon: Clock, color: 'text-white/60' };
        return (
            <Badge variant="outline" className="flex items-center gap-1">
                <s.icon className={`h-3 w-3 ${s.color}`} />
                {s.label}
            </Badge>
        );
    };

    return (
        <div>
            <h1 className="text-2xl font-bold uppercase tracking-wide text-white mb-6">Mis Pedidos</h1>

            {loading ? (
                <p className="text-white/60 text-center py-8">Cargando...</p>
            ) : orders.length === 0 ? (
                <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                    <CardContent className="py-12 text-center">
                        <Package className="h-12 w-12 text-white/60 mx-auto mb-4" />
                        <p className="text-white/60">No realizaste ningún pedido todavía.</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {orders.map(order => (
                        <Card key={order.id} className="bg-[#0a0a0a] border-[#3a3a3f] hover:bg-white/5 transition-colors">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-sm font-bold text-white">{order.order_number}</span>
                                            {statusBadge(order.status)}
                                        </div>
                                        <p className="text-xs text-white/60 mt-1">{order.items_count} producto{order.items_count !== 1 ? 's' : ''} — ${parseFloat(order.total || 0).toFixed(2)}</p>
                                        {order.gym && <p className="text-xs text-white/40 mt-0.5">{order.gym.name}</p>}
                                        <p className="text-xs text-white/40 mt-0.5">{formatDateTimeShort(order.created_at)}</p>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0 ml-3">
                                        {order.status === 'pending' && order.payment_method === 'mercadopago' && (
                                            <Button size="sm" onClick={() => handlePay(order.id)} disabled={payingId === order.id}>
                                                {payingId === order.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                                                Pagar
                                            </Button>
                                        )}
                                        <Button variant="ghost" size="icon" onClick={() => openDetail(order)} className="text-white hover:bg-white/5">
                                            <Eye className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#3a3a3f] text-white sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
                            <Package className="h-5 w-5" />
                            Pedido {selectedOrder?.order_number}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedOrder && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Estado</Label>
                                    <div className="mt-1">{statusBadge(selectedOrder.status)}</div>
                                </div>
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Total</Label>
                                    <p className="text-sm font-semibold text-white mt-1">${parseFloat(selectedOrder.total || 0).toFixed(2)}</p>
                                </div>
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Método de pago</Label>
                                    <p className="text-sm text-white/60 mt-1 capitalize">{selectedOrder.payment_method || '-'}</p>
                                </div>
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Estado del pago</Label>
                                    <p className="text-sm text-white/60 mt-1 capitalize">{selectedOrder.payment_status || '-'}</p>
                                </div>
                            </div>
                            {selectedOrder.notes && (
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Notas</Label>
                                    <p className="text-sm text-white/80 mt-1">{selectedOrder.notes}</p>
                                </div>
                            )}
                            <div>
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Productos</Label>
                                <div className="space-y-2 mt-1">
                                    {selectedOrder.items?.map(item => (
                                        <div key={item.id} className="flex items-center justify-between p-2 rounded bg-white/5 border border-[#3a3a3f]">
                                            <div>
                                                <p className="text-sm text-white">{item.product?.name || 'Producto'}</p>
                                                <p className="text-xs text-white/60">${parseFloat(item.product_price || 0).toFixed(2)} x {item.quantity}</p>
                                            </div>
                                            <span className="text-sm font-semibold text-white">${(parseFloat(item.product_price || 0) * parseInt(item.quantity || 0)).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {selectedOrder.status_histories?.length > 0 && (
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Historial</Label>
                                    <div className="space-y-1 mt-1">
                                        {selectedOrder.status_histories.map(h => (
                                            <div key={h.id} className="flex items-center justify-between text-xs text-white/60">
                                                <span>
                                                    {STATUS_MAP[h.from_status]?.label || h.from_status || '-'} → {STATUS_MAP[h.to_status]?.label || h.to_status}
                                                </span>
                                                <span>{formatDateTimeShort(h.created_at)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDetailOpen(false)} className="border-[#3a3a3f] text-white hover:bg-white/5">Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
